/**
 * Sorting helpers for the date-keyed pickers (WI-1239).
 *
 * Every fund-report / fund-valuation picker sorts on a `TraceableDate.date`, which is an ISO string — so a
 * plain `localeCompare` is already chronological and no parsing is needed. What they all also need is
 * "blanks last": a row whose date was never entered must sink to the bottom in BOTH directions, because it
 * is missing data rather than an extreme value. Expressing that inline meant the same twelve-line comparator
 * appeared once per picker.
 */

/**
 * Comparator over an ISO date string, blanks always last.
 *
 * `pick` reads the date off the row; blank means null, undefined or "". Direction flips only the comparison
 * between two present dates, never the placement of blanks.
 *
 * @example
 * [...valuations].sort(byIsoDateBlanksLast(v => v.closingDate?.date, "desc"))
 */
export const byIsoDateBlanksLast = <T>(
    pick: (item: T) => string | null | undefined,
    direction: "asc" | "desc" = "asc"
): ((a: T, b: T) => number) => {
    const dir = direction === "desc" ? -1 : 1;
    return (a: T, b: T): number => {
        const dateA = pick(a) ?? "";
        const dateB = pick(b) ?? "";
        if (!dateA && !dateB) {
            return 0;
        }
        if (!dateA) {
            return 1;
        }
        if (!dateB) {
            return -1;
        }
        return dateA.localeCompare(dateB) * dir;
    };
};

/** As {@link byIsoDateBlanksLast}, but returns a new sorted array instead of a comparator. */
export const sortByIsoDateBlanksLast = <T>(
    items: readonly T[],
    pick: (item: T) => string | null | undefined,
    direction: "asc" | "desc" = "asc"
): T[] => [...items].sort(byIsoDateBlanksLast(pick, direction));
